import {
  getFormatNumPlus,
  getSuitMaxNumber,
  toFirstUpperCase,
} from '@gp/utils/common';
import { ECHART_COLOR } from '@gp/constants/const';
import { BASE_ECHART_OPTION, Y_AXIS_NAME_CURRENCY_GRID_OPTION, Y_AXIS_NAME_CURRENCY_OPTION } from '@gp/constants/echarts';
import moment from 'moment';
import { isOthers, OTHERS_TEXT } from '../../../utils';

const getNum = (symbol: string): number => {
  let res = 1;
  switch (symbol) {
    case 'K':
      res = 1000;

    case 'M':
      res = 1000000;

    case 'B':
      res = 1000000000;

    default:
      res = 1;
  }
  return res;
}

const getEChartOpt = (
  data: {
    'date': string,
    'others': string,
    'total_budget': string,
    [keyName: string]: string,
  }[] | null,
  keysMap: {
    'metric': string[], // 指标名称
    'metric_key': string[], // 对应字段名称
  },
  yAxisLeftFormatter: Function,
  length = 11,
) => {
  const xAxisLabelList: string[] = []; // x轴刻度名列表
  // const itemDatas: number[][] = Array.from({length: 7}, () => ([]));
  const itemDatas: number[][] = Array.from({ length }, () => ([]));

  let leftPositiveMax = 0;
  let leftNegativeMax = 0;
  if (Array.isArray(data) && data?.length) {
    for (let i = 0; i < data?.length; i++) {
      const t: any = data?.[i];
      const isMonth = moment(t?.date)?.isValid();
      xAxisLabelList.push(isMonth ? moment(t?.date)?.format('YYYY-MM') : t?.date);
      let tempLeftPositiveMax = 0, tempLeftNegativeMax = 0;
      for (let j = 0, len = itemDatas?.length; j < len; j++) {
        const v = t?.[keysMap?.['metric_key']?.[j]];
        itemDatas[j].push(v);
        if (v > 0) {
          tempLeftPositiveMax += v;
        }
        if (v < 0) {
          tempLeftNegativeMax += -v;
        }
        if (j === (len - 1)) {
          if (tempLeftPositiveMax > leftPositiveMax) { leftPositiveMax = tempLeftPositiveMax; }
          if (tempLeftNegativeMax > leftNegativeMax) { leftNegativeMax = tempLeftNegativeMax; }
        }
      }
    }
  }

  const maxSplitNum = 5;
  // 正数及负数可以完整分割的所有段数
  const leftPositiveYSplitNumList: number[] = [],
    leftNegativeYSplitNumList: number[] = [];

  const symbol1 = getFormatNumPlus(leftPositiveMax)?.slice(-1);
  const num1 = getNum(symbol1);
  for (let i = 2; i <= leftPositiveMax; i++) {
    if (i > maxSplitNum) break;
    if ((leftPositiveMax % (i * num1)) === 0) {
      leftPositiveYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  const symbol2 = getFormatNumPlus(Math.abs(leftNegativeMax))?.slice(-1);
  const num2 = getNum(symbol2);
  for (let i = 2; i <= Math.abs(leftNegativeMax); i++) {
    if (i > maxSplitNum) break;
    if ((leftNegativeMax % (i * num2)) === 0) {
      leftNegativeYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  leftPositiveYSplitNumList?.reverse();
  let leftPositiveSplitNum = leftPositiveYSplitNumList?.[0] || maxSplitNum;
  leftNegativeYSplitNumList?.reverse();
  let leftNegativeSplitNum = leftNegativeYSplitNumList?.[0] || maxSplitNum;

  /**
   * 左侧部分计算
   */
  leftPositiveMax = getSuitMaxNumber(leftPositiveMax, leftPositiveSplitNum);
  const leftPositiveSplitInterval = Math.ceil(leftPositiveMax / leftPositiveSplitNum);
  leftNegativeMax = getSuitMaxNumber(Math.abs(leftNegativeMax), leftNegativeSplitNum);
  const leftNegativeSplitInterval = Math.ceil(leftNegativeMax / leftNegativeSplitNum);
  const leftRealSplitInterval = Math.max(leftPositiveSplitInterval, leftNegativeSplitInterval, 0);
  if (leftRealSplitInterval > leftPositiveSplitInterval) {
    leftPositiveMax = leftPositiveSplitNum * leftRealSplitInterval;
  }
  if (leftRealSplitInterval > leftNegativeSplitInterval) {
    leftNegativeMax = leftNegativeSplitNum * leftRealSplitInterval;
  }

  const isHasNegative = leftNegativeMax > 0, isAllNegative = leftPositiveMax === 0 && leftNegativeMax > 0;
  const splitNum = isHasNegative ? leftPositiveSplitNum + leftNegativeSplitNum : leftPositiveSplitNum;

  const _series = [];
  for (let i = 0, len = itemDatas?.length; i < len; i++) {
    if (i > (keysMap?.['metric']?.length - 1)) break; // 返回的数据不足7个图例时
    const t = itemDatas?.[i];
    const name = keysMap?.['metric']?.[i];
    const isTotalBudget = keysMap?.['metric_key']?.[i] == 'total_budget';
    let seriesData: {[keyName: string]: any}[] | null = null;
    for (let j = 0; j < t?.length; j++) {
      const k = t?.[j];
      seriesData = seriesData
        ? [
          ...seriesData,
          {
            name,
            value: k,
            isTotalBudget, // 标记告知浮窗Total Budget显示在第一行
            totalBudget: isTotalBudget ? k : '',
          },
        ]
        : [
          {
            name,
            value: k,
            isTotalBudget, // 标记告知浮窗Total Budget显示在第一行
            totalBudget: isTotalBudget ? k : '',
          },
        ];
    }
    // 线
    if (isTotalBudget) {
      if (Array.isArray(seriesData)) {
        seriesData = seriesData?.map((t, i) => {
          // 前后两个点没有数据且当前点有数据时，当前数据点显示出来
          const flag = t?.value && !seriesData?.[i - 1]?.value && !seriesData?.[i + 1]?.value;
          return {
            ...t,
            itemStyle: {
              opacity: flag ? 1 : 0,
            },
          };
        });
      }
      _series.push({
        name,
        type: 'line',
        symbol: 'circle',
        symbolSize: 6, // 拐点实心点大小
        color: ECHART_COLOR?.[7],
        yAxisIndex: 0,
        data: seriesData,
        emphasis: {
          // focus: 'series',
          scale: 1.25,
          itemStyle: {
            opacity: 1,
          },
        },
      });
      // 柱子
    } else {
      _series.push({
        name,
        type: 'bar',
        stack: 'metric',
        barMaxWidth: 64,
        barGap: 0,
        itemStyle: {
          color: ECHART_COLOR?.[i >= 7 ? i + 1 : i], // 这里确保跳过total的颜色不重复
        },
        yAxisIndex: 0,
        data: seriesData,
        /* emphasis: {
          focus: 'series',
        }, */
        label: {
          distance: 100,
        },
      });
    }
  }

  return {
    title: {
      show: false,
    },
    legend: {
      ...BASE_ECHART_OPTION.legend,
      formatter: (name: string): string => {
        if (isOthers(name)) return OTHERS_TEXT;
        return toFirstUpperCase(name);
      },
    },
    grid: {
      ...BASE_ECHART_OPTION.grid,
      ...Y_AXIS_NAME_CURRENCY_GRID_OPTION,
    },
    xAxis: {
      ...BASE_ECHART_OPTION.xAxis,
      show: true,
      type: 'category',
      data: xAxisLabelList,
      axisLabel: { // 刻度文案设置
        ...BASE_ECHART_OPTION.xAxis?.axisLabel,
        margin: 16,
      },
    },
    yAxis: [
      {
        ...Y_AXIS_NAME_CURRENCY_OPTION,
        type: 'value',
        scale: false,
        // name: '',
        splitLine: { // 分割线设置
          ...BASE_ECHART_OPTION.yAxis?.[0]?.splitLine,
        },
        axisLabel: { // 刻度文案设置
          ...BASE_ECHART_OPTION.yAxis?.[0]?.axisLabel,
          formatter: yAxisLeftFormatter,
        },
        // min: isHasNegative ? -Math.abs(leftNegativeMax) : 0,
        // splitNumber: splitNum,
        // interval: leftRealSplitInterval,
        // max: isAllNegative ? 0 : leftPositiveMax,
      },
    ],
    series: _series,
  };
}

// tooltip浮窗内容
const tootipFormatter = (params: any) => {
  const totalBudget = Array.isArray(params)
    ? params?.filter((t) => t?.data?.isTotalBudget)?.[0]?.data?.totalBudget || ''
    : '';
  let title = '',
    firItemListStr = '', // 第一列数据
    secItemListStr = '', // 第二列数据
    totalBudgetLeftStr = `
        <div style="display: flex; flex-wrap: nowrap; align-items: center; margin-top: 6px;">
          <span class='general-circular-dot' style="background-color: ${ECHART_COLOR?.[7]}; margin-right: 8px;"></span>
          <div class='general-item body-text' style="max-width: 124px; margin: 0px;">Total Budget</div>
        </div>
      `,
    totalBudgetRightStr = `
        <div class="body-text" style="line-height: 20px; text-align: right; margin-top: 6px;">${getFormatNumPlus(totalBudget)
      }</div>
      `,
    totalSpend: number | undefined = undefined;

  if (Array.isArray(params) && params?.length) {
    for (let i = 0; i < params?.length; i++) {
      const t = params?.[i];
      if (!title) title = t?.axisValueLabel;
      if (!t?.data?.isTotalBudget) {
        if (t?.data?.value || t?.data?.value === 0) {
          totalSpend = (totalSpend || 0) + (Number(t?.data?.value) || 0);
        }
        let seriesName = t?.seriesName;
        seriesName = isOthers(seriesName) ? OTHERS_TEXT : seriesName;
        firItemListStr += `
          <div style="display: flex; flex-wrap: nowrap; align-items: center; margin-top: 6px;">
            <span class='general-circular-dot' style="background-color: ${t?.color}; margin-right: 8px;"></span>
            <div class='general-item body-text' style=" max-width: 124px; margin: 0px;">${seriesName}</div>
          </div>
        `;
        secItemListStr += `
          <div class='general-item body-num' style="line-height: 20px; text-align: right; margin-top: 6px;">${getFormatNumPlus(t?.data?.value)
          }</div>
        `;
      }
    }
  }

  const htmlStr = `
    <section class='echart-tooltip-style-content'>
      <div class='general-item' style="width: 100%; height: auto; margin: 0px; display: flex; justify-content: space-between;">
        <div>
        <span class='header-title '>${title || '-'}</span>
          <span class='body-text'>${totalBudgetLeftStr || '-'}</span>
          <div style="width: 100%; height: 1px; background-color: rgb(216 218 228 / 30%); margin: 12px 0;"></div>
          <div style="display: flex; flex-wrap: nowrap; align-items: center;">
            <div class='general-item body-text' style=" max-width: 124px; margin: 0px;">Total Spend</div>
          </div>
          ${firItemListStr}
        </div>
        <div style="margin-left: 30px; text-align: right">
          <span class='header-title '>Cost</span>
          <span class='body-text'>${totalBudgetRightStr || '-'}</span>
          <div style="width: calc(100% + 40px); height: 1px; background-color: rgb(216 218 228 / 30%); margin: 12px 0 12px -30px;"></div>
          <div style="line-height: 20px; text-align: right;">${getFormatNumPlus(totalSpend || '')
    }</div>
          ${secItemListStr}
        </div>
      </div>
    <section>
  `;
  return htmlStr;
}

export {
  getEChartOpt,
  tootipFormatter,
}
